/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react'
import PropTypes from 'prop-types'
import {useItemVariant} from '../item-variant'
import {getPriceData} from '../../utils/product-utils'
import DisplayPrice from '../display-price'
import PromoCallout from '../product-tile/promo-callout'

/**
 * ItemPrice is a React functional component that calculates and displays the price information
 * for a given item variant. It utilizes the `useItemVariant` hook to obtain variant details
 * and determines the appropriate pricing data to display, taking into account any applicable
 * discounts or promotions.
 *
 * @param {Object} props - The properties passed to the component.
 * @param {string} props.currency - The currency in which the price should be displayed.
 * @param {Object} props.conversionsRate - Conversion rate for orders on account pages (they coud be created with another ship-to flow).
 * @param {string} props.shipToCountryCode - The country code to which the item is shipped (it's for orders on account pages since they coud be created with another ship-to flow).
 * @param {...Object} props - Additional properties that can be passed to the component.
 *
 * @returns {JSX.Element} A JSX element that renders the price information using the DisplayPrice component.
 *
 * The component handles different scenarios for pricing:
 * - If the variant has basket pricing, it prioritizes these prices for display, as they may include
 *   promotions or discounts.
 * - If the variant is part of a set or is a master variant, it adjusts the pricing display accordingly.
 * - For wishlist pages, it extracts price information directly from the variant or product object.
 *
 * The component also includes accessibility features by providing a label for screen readers
 * and integrates promotional callouts when applicable.
 */
const ItemPrice = ({
    currency,
    quantity,
    isRenderPricePerUnit = true,
    isExchangeInvoicePrice = false,
    conversionsRate,
    shipToCountryCode,
    priceData: propPriceData,
    ...props
}) => {
    const variant = useItemVariant()
    const price = variant?.price
    const priceAfterItemDiscount = variant?.priceAfterItemDiscount
    const priceAfterOrderDiscount = variant?.priceAfterOrderDiscount

    // priceAfterOrderDiscount as currentPrice on exchange order invoice for exchanhe order
    const currentPrice = isExchangeInvoicePrice ? priceAfterOrderDiscount : priceAfterItemDiscount

    const isASet = variant?.type?.set
    const isMaster = variant?.type?.master
    let priceData
    let qty = quantity || variant?.quantity
    const isOnSale = price > priceAfterItemDiscount

    // propPriceData: case with initial order on exchange order invoice
    if (propPriceData) {
        priceData = propPriceData
    } else if (variant?.itemId) {
        // When variant has basket pricing, we should prioritize these prices for displaying
        // since they may have take promotion/discount into account
        // NOTE: try NOT to re-calculate these values since basket-level discount is complicated
        priceData = {
            currentPrice: currentPrice,
            beforePromoPrice: isOnSale ? price : null,
            pricePerUnit: currentPrice / qty,
            isASet,
            isMaster,
            isRange: isASet || isMaster || false,
            isOnSale: isOnSale
        }
    } else {
        // for wishlist page we extract price info from variant/product obj
        priceData = getPriceData(variant)
    }

    return (
        <DisplayPrice
            labelForA11y={variant?.name}
            currency={currency}
            priceData={priceData}
            quantity={qty}
            isRenderPricePerUnit={isRenderPricePerUnit}
            isExchangeInvoicePrice={isExchangeInvoicePrice}
            conversionsRate={conversionsRate}
            shipToCountryCode={shipToCountryCode}
            promoCallout={<PromoCallout priceData={priceData} product={variant} />}
            {...props}
        />
    )
}

ItemPrice.propTypes = {
    currency: PropTypes.string.isRequired,
    quantity: PropTypes.number,
    isRenderPricePerUnit: PropTypes.bool,
    isExchangeInvoicePrice: PropTypes.bool,
    conversionsRate: PropTypes.object,
    shipToCountryCode: PropTypes.string,
    priceData: PropTypes.object
}

export default ItemPrice
